/**
 * /me/leaves — Employee Leave Hub (M3X.LEAVES PR3 — F.2).
 *
 * Per pack §1.13. Colored balance cards (one per leave type),
 * upcoming + recent leaves list, "Request a Leave" CTA.
 *
 * Two resolution paths for "which employee am I":
 *   1. URL `?employee_id=<uuid>` — HR drilling-in from the Employees
 *      page, OR direct testing.
 *   2. M3X.AUTH `/api/me` — the calling Clerk user mapped to their
 *      Employee row. Lands once the user signs in. The banner only
 *      shows when both fail (unknown user, no link in the tenant).
 */
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui/PageHeader";
import { api, apiFetch } from "../lib/api";

interface MyEmployeeDTO {
  employee_id: string;
  employee_code: string | null;
  full_name_en: string | null;
  full_name_ar: string | null;
  legal_entity_id: string | null;
}

export function MyLeaveHubPage() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const urlEmployeeId = search.get("employee_id") ?? "";

  // Skip /api/me when the URL already names an employee — HR drill-in
  // path stays zero-extra-roundtrip.
  const meQ = useQuery({
    queryKey: ["my-employee"],
    enabled: !urlEmployeeId,
    retry: false,
    queryFn: async (): Promise<MyEmployeeDTO | null> => {
      const res = await apiFetch("/api/me");
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`my-employee ${res.status}`);
      return res.json();
    },
  });

  if (urlEmployeeId) return <Body employeeId={urlEmployeeId} />;
  if (meQ.isLoading) return <Loading />;
  if (meQ.data?.employee_id) return <Body employeeId={meQ.data.employee_id} />;

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
      {t(
        "leave_hub.missing_employee_id",
        "Sign in to view your leaves. HR can view any employee's leaves from the Employees page.",
      )}
    </div>
  );
}

function Body({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();

  // Balances are queried by employee via existing /employees/{id}/leave/balance.
  // For v1 we render the requests list (which exists); balance cards
  // come once an /api/me/leave-balances aggregator is added in a follow-up.
  const balances = useQuery<unknown[]>({
    queryKey: ["my-leave-balances", employeeId],
    queryFn: async () => [],  // placeholder until aggregator endpoint exists
  });

  const requests = useQuery({
    queryKey: ["my-leave-requests", employeeId],
    queryFn: () => api.listLeaveRequests({ employeeId }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("leave_hub.title", "My Leaves")}
        subtitle={t(
          "leave_hub.subtitle",
          "Your leave balances, upcoming and recent requests.",
        )}
      />

      <div className="flex justify-end">
        <Link
          to={`/me/leaves/new?employee_id=${encodeURIComponent(employeeId)}`}
          className="rounded-md bg-brand-blue text-white px-4 py-2 text-sm font-medium hover:bg-brand-deep"
        >
          {t("leave_hub.request_cta", "Request a Leave")}
        </Link>
      </div>

      <section>
        <h3 className="text-lg font-semibold mb-3">
          {t("leave_hub.balances_title", "Your balances")}
        </h3>
        {balances.isLoading ? <Loading /> : null}
        {balances.error ? (
          <ErrorPanel
            message={t(
              "leave_hub.balances_error",
              "Couldn't load leave balances.",
            )}
          />
        ) : null}
        {balances.data && balances.data.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-text-tertiary">
            {t("leave_hub.no_balances", "No leave balances on record yet.")}
          </div>
        ) : null}
        {balances.data && balances.data.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {balances.data.map((b: any) => (
              <BalanceCard key={b.id} balance={b} />
            ))}
          </div>
        ) : null}
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">
          {t("leave_hub.recent_title", "Recent requests")}
        </h3>
        {requests.data && requests.data.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-text-tertiary">
            {t("leave_hub.no_requests", "No leave requests yet.")}
          </div>
        ) : null}
        {requests.data && requests.data.length > 0 ? (
          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-start p-2 font-medium text-text-tertiary">
                    {t("leave_hub.col_type", "Type")}
                  </th>
                  <th className="text-start p-2 font-medium text-text-tertiary">
                    {t("leave_hub.col_dates", "Dates")}
                  </th>
                  <th className="text-start p-2 font-medium text-text-tertiary">
                    {t("leave_hub.col_state", "State")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.data.slice(0, 10).map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-slate-200 hover:bg-slate-50"
                  >
                    <td className="p-2">
                      {(r.leave_type_id ?? "").slice(0, 8)}
                    </td>
                    <td className="p-2 text-text-tertiary">
                      {r.start_date} → {r.end_date}
                    </td>
                    <td className="p-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-100">
                        {r.workflow_state}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function BalanceCard({ balance }: { balance: any }) {
  // Colored card — color comes from leave_type.leave_color (passed via API)
  const color = balance.leave_color ?? "#64748b";
  return (
    <div
      className="rounded-lg border-2 p-4 bg-surface-default"
      style={{ borderColor: color }}
    >
      <div
        className="text-xs font-semibold uppercase mb-1"
        style={{ color }}
      >
        {balance.leave_type_code ?? "—"}
      </div>
      <div className="text-2xl font-bold text-text-primary">
        {balance.remaining_days ?? balance.available_days ?? "—"}
      </div>
      <div className="text-xs text-text-tertiary">days remaining</div>
    </div>
  );
}
